/**
 * Animation Performance Monitoring System
 * Tracks FPS, memory usage, and animation performance with enhanced analytics
 */

import type { PerformanceMetrics, AnimationConfig } from './config';

interface PerformanceBudget {
  maxFPS: number;
  minFPS: number;
  maxMemoryMB: number;
  maxConcurrentAnimations: number;
  maxFrameDrops: number;
}

interface PerformanceAnalytics {
  sessionStart: number;
  totalAnimations: number;
  averageFPS: number;
  peakMemoryUsage: number;
  performanceViolations: number;
  deviceInfo: {
    cores: number;
    memory: number;
    connection: string;
  };
}

export class AnimationPerformanceMonitor {
  private frameCount = 0;
  private lastTime = 0;
  private fps = 60;
  private activeAnimations = 0;
  private droppedFrames = 0;
  private memoryUsage = 0;
  private isMonitoring = false;
  private rafId: number | null = null;
  private config: AnimationConfig;
  private callbacks: Array<(metrics: PerformanceMetrics) => void> = [];
  
  // Enhanced monitoring properties
  private performanceBudget: PerformanceBudget;
  private analytics: PerformanceAnalytics;
  private fpsHistory: number[] = [];
  private memoryHistory: number[] = [];
  private animationRegistry = new Map<string, { startTime: number; element?: Element }>();
  private cleanupTasks = new Set<() => void>();
  private budgetViolationCallbacks: Array<(violation: string, severity: 'low' | 'medium' | 'high') => void> = [];

  constructor(config: AnimationConfig) {
    this.config = config;
    
    // Initialize performance budget based on config
    this.performanceBudget = {
      maxFPS: 60,
      minFPS: config.performance === 'high' ? 55 : config.performance === 'medium' ? 45 : 30,
      maxMemoryMB: config.performance === 'high' ? 100 : config.performance === 'medium' ? 75 : 50,
      maxConcurrentAnimations: config.maxConcurrentAnimations,
      maxFrameDrops: config.performance === 'high' ? 5 : config.performance === 'medium' ? 10 : 20,
    };
    
    // Initialize analytics
    this.analytics = {
      sessionStart: Date.now(),
      totalAnimations: 0,
      averageFPS: 60,
      peakMemoryUsage: 0,
      performanceViolations: 0,
      deviceInfo: {
        cores: navigator.hardwareConcurrency || 4,
        memory: (navigator as any).deviceMemory || 4,
        connection: (navigator as any).connection?.effectiveType || '4g',
      },
    };
    
    // Setup automatic cleanup detection
    this.setupMemoryLeakDetection();
  }

  /**
   * Start performance monitoring
   */
  start(): void {
    if (this.isMonitoring) return;
    
    this.isMonitoring = true;
    this.lastTime = performance.now();
    this.measureFrame();
  }

  /**
   * Stop performance monitoring
   */
  stop(): void {
    this.isMonitoring = false;
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  /**
   * Register animation start with enhanced tracking
   */
  registerAnimation(animationId?: string, element?: Element): string {
    this.activeAnimations++;
    this.analytics.totalAnimations++;
    
    const id = animationId || `anim_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.animationRegistry.set(id, {
      startTime: performance.now(),
      element,
    });
    
    // Check for budget violations
    if (this.activeAnimations > this.performanceBudget.maxConcurrentAnimations) {
      this.reportBudgetViolation('Too many concurrent animations', 'high');
    }
    
    return id;
  }

  /**
   * Register animation end with cleanup
   */
  unregisterAnimation(animationId?: string): void {
    this.activeAnimations = Math.max(0, this.activeAnimations - 1);
    
    if (animationId && this.animationRegistry.has(animationId)) {
      const animation = this.animationRegistry.get(animationId)!;
      const duration = performance.now() - animation.startTime;
      
      // Log long-running animations
      if (duration > 5000) {
        console.warn(`Long-running animation detected: ${animationId} (${duration}ms)`);
      }
      
      this.animationRegistry.delete(animationId);
    }
  }

  /**
   * Register cleanup task to prevent memory leaks
   */
  registerCleanupTask(cleanup: () => void): void {
    this.cleanupTasks.add(cleanup);
  }

  /**
   * Execute all cleanup tasks
   */
  executeCleanup(): void {
    this.cleanupTasks.forEach(cleanup => {
      try {
        cleanup();
      } catch (error) {
        console.error('Animation cleanup error:', error);
      }
    });
    this.cleanupTasks.clear();
  }

  /**
   * Get current performance metrics
   */
  getMetrics(): PerformanceMetrics {
    return {
      fps: this.fps,
      memoryUsage: this.memoryUsage,
      activeAnimations: this.activeAnimations,
      droppedFrames: this.droppedFrames,
    };
  }

  /**
   * Subscribe to performance updates
   */
  subscribe(callback: (metrics: PerformanceMetrics) => void): () => void {
    this.callbacks.push(callback);
    
    return () => {
      const index = this.callbacks.indexOf(callback);
      if (index > -1) {
        this.callbacks.splice(index, 1);
      }
    };
  }

  /**
   * Check if performance is within acceptable limits
   */
  isPerformanceGood(): boolean {
    const metrics = this.getMetrics();
    return (
      metrics.fps >= 55 && // Allow slight FPS drops
      metrics.activeAnimations <= this.config.maxConcurrentAnimations &&
      metrics.droppedFrames < 5 // Allow some dropped frames
    );
  }

  /**
   * Get performance recommendations with enhanced analysis
   */
  getRecommendations(): string[] {
    const metrics = this.getMetrics();
    const recommendations: string[] = [];

    if (metrics.fps < this.performanceBudget.minFPS) {
      recommendations.push('Consider reducing animation complexity or duration');
      recommendations.push('Try disabling complex effects or reducing concurrent animations');
    }

    if (metrics.activeAnimations > this.performanceBudget.maxConcurrentAnimations) {
      recommendations.push('Too many concurrent animations - consider staggering or reducing');
    }

    if (metrics.droppedFrames > this.performanceBudget.maxFrameDrops) {
      recommendations.push('High frame drop rate - check for heavy computations during animations');
      recommendations.push('Consider using transform and opacity properties for better performance');
    }

    if (metrics.memoryUsage > this.performanceBudget.maxMemoryMB) {
      recommendations.push('High memory usage - ensure animations are properly cleaned up');
      recommendations.push('Check for memory leaks in animation event listeners');
    }

    // Device-specific recommendations
    if (this.analytics.deviceInfo.cores < 4) {
      recommendations.push('Low-end device detected - consider reducing animation complexity');
    }

    if (this.analytics.deviceInfo.connection === '3g' || this.analytics.deviceInfo.connection === '2g') {
      recommendations.push('Slow connection detected - consider reducing asset-heavy animations');
    }

    return recommendations;
  }

  /**
   * Get comprehensive analytics
   */
  getAnalytics(): PerformanceAnalytics & { 
    currentMetrics: PerformanceMetrics;
    performanceBudget: PerformanceBudget;
    fpsHistory: number[];
    memoryHistory: number[];
  } {
    return {
      ...this.analytics,
      currentMetrics: this.getMetrics(),
      performanceBudget: this.performanceBudget,
      fpsHistory: [...this.fpsHistory],
      memoryHistory: [...this.memoryHistory],
    };
  }

  /**
   * Subscribe to budget violation alerts
   */
  onBudgetViolation(callback: (violation: string, severity: 'low' | 'medium' | 'high') => void): () => void {
    this.budgetViolationCallbacks.push(callback);
    
    return () => {
      const index = this.budgetViolationCallbacks.indexOf(callback);
      if (index > -1) {
        this.budgetViolationCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Update performance budget
   */
  updateBudget(budget: Partial<PerformanceBudget>): void {
    this.performanceBudget = { ...this.performanceBudget, ...budget };
  }

  /**
   * Force cleanup of stale animations
   */
  forceCleanup(): void {
    const now = performance.now();
    const staleAnimations: string[] = [];
    
    this.animationRegistry.forEach((animation, id) => {
      if (now - animation.startTime > 30000) { // 30 seconds
        staleAnimations.push(id);
      }
    });
    
    staleAnimations.forEach(id => {
      console.warn(`Cleaning up stale animation: ${id}`);
      this.unregisterAnimation(id);
    });
    
    this.executeCleanup();
  }

  private measureFrame = (): void => {
    if (!this.isMonitoring) return;

    const currentTime = performance.now();
    const deltaTime = currentTime - this.lastTime;

    // Calculate FPS
    this.frameCount++;
    if (deltaTime >= 1000) {
      this.fps = Math.round((this.frameCount * 1000) / deltaTime);
      this.frameCount = 0;
      this.lastTime = currentTime;

      // Update FPS history (keep last 60 seconds)
      this.fpsHistory.push(this.fps);
      if (this.fpsHistory.length > 60) {
        this.fpsHistory.shift();
      }

      // Check for dropped frames
      if (this.fps < this.performanceBudget.minFPS) {
        this.droppedFrames++;
        this.reportBudgetViolation(`Low FPS detected: ${this.fps}`, 'medium');
      }

      // Measure memory usage if available
      if ('memory' in performance) {
        const memory = (performance as any).memory;
        this.memoryUsage = memory.usedJSHeapSize / 1024 / 1024; // Convert to MB
        
        // Update memory history
        this.memoryHistory.push(this.memoryUsage);
        if (this.memoryHistory.length > 60) {
          this.memoryHistory.shift();
        }
        
        // Update peak memory usage
        if (this.memoryUsage > this.analytics.peakMemoryUsage) {
          this.analytics.peakMemoryUsage = this.memoryUsage;
        }
        
        // Check memory budget
        if (this.memoryUsage > this.performanceBudget.maxMemoryMB) {
          this.reportBudgetViolation(`High memory usage: ${this.memoryUsage.toFixed(1)}MB`, 'high');
        }
      }

      // Update average FPS
      if (this.fpsHistory.length > 0) {
        this.analytics.averageFPS = this.fpsHistory.reduce((a, b) => a + b, 0) / this.fpsHistory.length;
      }

      // Periodic cleanup check
      if (this.frameCount % 300 === 0) { // Every 5 minutes
        this.forceCleanup();
      }

      // Notify subscribers
      const metrics = this.getMetrics();
      this.callbacks.forEach(callback => callback(metrics));
    }

    this.rafId = requestAnimationFrame(this.measureFrame);
  };

  private reportBudgetViolation(violation: string, severity: 'low' | 'medium' | 'high'): void {
    this.analytics.performanceViolations++;
    this.budgetViolationCallbacks.forEach(callback => callback(violation, severity));
    
    if (severity === 'high') {
      console.warn(`Performance Budget Violation (${severity}):`, violation);
    }
  }

  private setupMemoryLeakDetection(): void {
    // Detect potential memory leaks by monitoring animation registry growth
    setInterval(() => {
      if (this.animationRegistry.size > 100) {
        console.warn(`Large animation registry detected: ${this.animationRegistry.size} animations`);
        this.forceCleanup();
      }
    }, 30000); // Check every 30 seconds
  }
}

/**
 * Global performance monitor instance
 */
let globalMonitor: AnimationPerformanceMonitor | null = null;

/**
 * Get or create the global performance monitor
 */
export const getPerformanceMonitor = (config: AnimationConfig): AnimationPerformanceMonitor => {
  if (!globalMonitor) {
    globalMonitor = new AnimationPerformanceMonitor(config);
  }
  return globalMonitor;
};

/**
 * Hook for tracking animation lifecycle with enhanced features
 */
export const useAnimationTracking = () => {
  return {
    onAnimationStart: (id?: string, element?: Element) => globalMonitor?.registerAnimation(id, element),
    onAnimationComplete: (id?: string) => globalMonitor?.unregisterAnimation(id),
    registerCleanup: (cleanup: () => void) => globalMonitor?.registerCleanupTask(cleanup),
  };
};

/**
 * Get performance analytics
 */
export const getPerformanceAnalytics = () => {
  return globalMonitor?.getAnalytics() || null;
};

/**
 * Subscribe to performance budget violations
 */
export const onPerformanceBudgetViolation = (
  callback: (violation: string, severity: 'low' | 'medium' | 'high') => void
) => {
  return globalMonitor?.onBudgetViolation(callback) || (() => {});
};